import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, Award, Pencil, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface SalesTier {
  id: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
}

export default function SalesTiersSettingsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", isActive: true });

  const { data: tiers = [] } = useQuery<SalesTier[]>({
    queryKey: ["/api/sales-tiers", { includeInactive: true }],
    queryFn: () => api.get<SalesTier[]>(`/sales-tiers?includeInactive=true`, sessionId),
  });

  const saveMutation = useMutation({
    mutationFn: () => editingId
      ? api.patch(`/sales-tiers/${editingId}`, form, sessionId)
      : api.post(`/sales-tiers`, form, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sales-tiers"] });
      setDialogOpen(false);
      setEditingId(null);
      toast({ title: editingId ? "Tier updated" : "Tier created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/sales-tiers/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sales-tiers"] });
      toast({ title: "Tier deleted" });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (tierIds: string[]) => api.post(`/sales-tiers/reorder`, { tierIds }, sessionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/sales-tiers"] }),
  });

  const handleEdit = (t: SalesTier) => {
    setEditingId(t.id);
    setForm({ name: t.name, isActive: t.isActive });
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm({ name: "", isActive: true });
    setDialogOpen(true);
  };

  const handleDelete = (t: SalesTier) => {
    if (confirm(`Delete sales tier "${t.name}"?`)) {
      deleteMutation.mutate(t.id);
    }
  };

  const handleMove = (index: number, dir: "up" | "down") => {
    const newIndex = dir === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= tiers.length) return;
    const newOrder = [...tiers];
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    reorderMutation.mutate(newOrder.map(t => t.id));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sales Tiers"
        description="Categorize customers into account tiers for segmentation"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNew} data-testid="button-new-tier">
              <Plus className="h-4 w-4 mr-1" /> Add Tier
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Award className="h-4 w-4" /> All Tiers ({tiers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tiers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No sales tiers configured.</p>
          ) : (
            <div className="space-y-2">
              {tiers.map((t, idx) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/40"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex flex-col">
                      <Button
                        variant="ghost" size="icon" className="h-5 w-5"
                        disabled={idx === 0}
                        onClick={() => handleMove(idx, "up")}
                      >
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-5 w-5"
                        disabled={idx === tiers.length - 1}
                        onClick={() => handleMove(idx, "down")}
                      >
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">#{t.displayOrder}</span>
                        <span className="font-medium text-sm">{t.name}</span>
                        {!t.isActive && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(t)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(t)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Sales Tier" : "New Sales Tier"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Gold"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Active
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!form.name || saveMutation.isPending}
              >
                {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Save Changes" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
